<template>
  <v-container fluid>
    <v-layout row wrap>
      <v-flex xs12>
        <v-card>
          <v-toolbar flat dense color="primary" dark>
            <v-toolbar-title>
              {{ $tc('models.notification', 2) }}
            </v-toolbar-title>
            <v-spacer />
            <v-btn flat @click="dialogForm = true">
              {{ $t('action.buttons.new') }}
            </v-btn>
            <v-btn
              v-if="id"
              icon
              dark
              :loading="loading"
              @click="fetchUserNotifications"
            >
              <v-icon>refresh</v-icon>
            </v-btn>
          </v-toolbar>
          <v-toolbar card flat dense color="white">
            <v-text-field
              v-model="match"
              flat
              hide-details
              :placeholder="$t('action.buttons.search')"
              prepend-icon="search"
              solo
            />
          </v-toolbar>
          <v-divider />
          <v-card-text class="pa-0">
            <v-data-table
              :headers="headers"
              :items="formatList"
              item-key="_id"
              :loading="loading"
              :rows-per-page-items="[8]"
              :total-items="pagination.totalItems"
              :pagination.sync="pagination"
            >
              <template slot="items" slot-scope="props">
                <td class="text-xs-center px-0">
                  <template v-if="!props.item.send && !props.item.sendMail">
                    <v-icon
                      v-if="can('edit', 'notification')"
                      color="success"
                      class="mr-1"
                      @click="setOne(props.item._id)"
                    >
                      edit
                    </v-icon>
                    <v-icon
                      v-if="can('delete', 'notification')"
                      color="error"
                      @click="ifDelete(props.item._id)"
                    >
                      delete
                    </v-icon>
                  </template>
                  <v-icon
                    v-else
                    color="primary"
                    class="mr-1"
                    @click="view(props.item._id)"
                  >
                    visibility
                  </v-icon>
                </td>

                <td>{{ props.item.title }}</td>
                <td>
                  <template v-for="(type, index) in props.item.sendType">
                    <v-chip :key="index + type" small color="primary" dark>
                      {{ type }}
                    </v-chip>
                  </template>
                  <span v-if="!props.item.sendType.length">--</span>
                </td>
                <td class="text-xs-center">
                  <button-state
                    :state="props.item.send || props.item.sendMail || false"
                  />
                </td>
                <td class="text-xs-center" style="min-width: 180px">
                  {{ props.item.sendDate }}
                </td>
                <td style="min-width: 150px">
                  {{ props.item.createdBy || '--' }}
                </td>
                <td class="text-xs-center" style="min-width: 180px">
                  {{ props.item.createdAt }}
                </td>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
    <notification-form
      v-if="dialogForm"
      :dialog="true"
      :user-id="id"
      @close="onCloseForm"
    />
    <notification-view v-if="dialogView" :dialog="true" @close="onCloseView" />
    <dialog-delete
      :dialog.sync="dialogDelete"
      :loading="loading"
      @delete="_setDelete(idDelete)"
    />
  </v-container>
</template>

<script>
import { debounce } from 'debounce'
import { mapActions } from 'vuex'
import NotificationForm from '@/components/apps/notifications/FormManager.vue'
import NotificationView from '@/components/apps/notifications/NotificationView.vue'
import DialogDelete from '@/components/widgets/dialog/DialogDelete.vue'
import { apiAxios } from '@/helpers'
import { formatDate } from '@/helpers/dateHelper'

export default {
  name: 'TabNotifications',
  components: {
    NotificationForm,
    NotificationView,
    DialogDelete,
  },
  props: {
    id: { type: String, required: true },
  },
  data() {
    return {
      items: [],
      match: '',
      loading: false,
      pagination: {
        page: 1,
        rowsPerPage: 10,
        totalItems: 0,
        sortBy: 'createdAt',
        descending: true,
      },
      dialogForm: false,
      dialogView: false,
      dialogDelete: false,
      idDelete: null,
    }
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t('fields.global.actions'),
          align: 'center',
          sortable: false,
        },
        {
          text: this.$t('titles.notifications.title'),
          value: 'title',
          sortable: false,
        },
        {
          text: this.$t('fields.notifications.sendType'),
          value: 'sendType',
          sortable: false,
        },
        {
          text: this.$t('titles.notifications.send'),
          align: 'center',
          value: 'send',
        },
        {
          text: this.$t('titles.notifications.sendDate'),
          align: 'center',
          value: 'sendDate',
        },

        {
          text: this.$t('fields.global.createdBy'),
          value: 'createdBy',
          sortable: false,
        },
        {
          text: this.$t('fields.global.createdAt'),
          align: 'center',
          value: 'createdAt',
        },
      ]
    },
    formatList() {
      return this.items.map((item) => ({
        ...item,
        sendDate: formatDate(item.sendDate, true),
        createdAt: formatDate(item.createdAt, true),
        sendType: this.formatSendType(item.sendType),
      }))
    },
  },
  watch: {
    match() {
      if (this.pagination.page !== 1) this.pagination.page = 1
      this.debounceFetch()
    },
    'pagination.page': {
      handler: function () {
        this.debounceFetch()
      },
    },
    'pagination.rowsPerPage': {
      handler: function () {
        this.debounceFetch()
      },
    },
    'pagination.sortBy': {
      handler: function () {
        this.debounceFetch()
      },
    },
    'pagination.descending': {
      handler: function () {
        this.debounceFetch()
      },
    },
  },
  mounted() {
    this.fetchUserNotifications()
  },
  methods: {
    ...mapActions({
      fetchOne: 'notifications/fetchOne',
      setDelete: 'notifications/delete',
      reset: 'notifications/reset',
      fetchReceivers: 'notifications/fetchReceivers',
      fetchStats: 'notifications/fetchStats',
    }),
    setOne(id) {
      this.$emit('clear')
      this.fetchOne(id).then(() => (this.dialogForm = true))
    },
    view(id) {
      this.fetchOne(id).then(() => {
        this.fetchReceivers(id)
        this.fetchStats(id)
        this.dialogView = true
      })
    },
    ifDelete(id) {
      this.idDelete = id
      this.dialogDelete = true
    },
    _setDelete(id) {
      this.setDelete(id)
      this.dialogDelete = false
    },
    formatSendType(value) {
      if (!value) return []
      const sendTypes = [
        {
          value: 'push',
          text: this.$t('fields.notifications.sendTypes.push'),
        },
        {
          value: 'email',
          text: this.$t('fields.notifications.sendTypes.email'),
        },
      ]
      return sendTypes
        .filter((item) => value.includes(item.value))
        .map((item) => item.text)
    },
    async fetchUserNotifications() {
      try {
        this.loading = true

        const filters = {
          userId: this.id,
          match: this.match,
          type: 'custom',
        }
        let params = {
          page: this.pagination.page,
          limit: this.pagination.rowsPerPage,
        }
        if (this.pagination.sortBy) {
          params.sort = {
            [this.pagination.sortBy]: this.pagination.descending ? -1 : 1,
          }
        }

        const { data } = await apiAxios.post('notifications/format', filters, {
          params,
        })
        if (data.data) {
          this.items = data.data.notification || []
          this.pagination.totalItems = data.paginate
            ? data.paginate.totalData
            : 0
        } else {
          this.items = []
          this.pagination.totalItems = 0
        }
      } finally {
        this.loading = false
      }
    },
    debounceFetch: debounce(function () {
      this.fetchUserNotifications()
    }, 700),
    onCloseForm() {
      this.reset()
      this.fetchUserNotifications()
      this.dialogForm = false
    },
    onCloseView() {
      this.reset()
      this.dialogView = false
    },
  },
}
</script>
